import { useEffect, useMemo, useState } from 'react';

import { GetUsersRequestDTO, MODULE, PdfScope } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { Link } from 'react-router-dom';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import QuestionIcon from '@/components/icons/question';
import SearchIcon from '@/components/icons/search';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { getSitemateUsersInfiniteQueryOptions, getSitemateUsersStatsQueryOptions, sitemateUserKeys } from '@/queries/users';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';

import { createSitemateUsersTableColumns } from './table-meta';

function SitemateUsersPage() {
  const { syncModuleColumns } = useModulesStore();
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounce(searchTerm, 500);

  const {
    config: { columnOrder, sorting },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.SitemateUsers, {
    columns: createSitemateUsersTableColumns(),
  });

  const {
    data: users,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetching: isFetchingUsers,
    isFetchingNextPage: isFetchingNextUsersPage,
    error: usersError,
  } = useInfiniteQuery(
    getSitemateUsersInfiniteQueryOptions({
      search: debouncedSearch,
      orderBy: sorting.key ? { field: sorting.key as NonNullable<GetUsersRequestDTO['orderBy']>['field'], direction: sorting.direction ?? 'asc' } : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedUsers = useMemo(() => {
    return (incomingData: typeof users) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const users = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => users[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedUsers = useMemo(() => getSelectedUsers(users), [users, getSelectedUsers]);

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.SitemateUsers, createSitemateUsersTableColumns());
  }, [syncModuleColumns]);

  useEffect(() => {
    if (usersError) {
      toast.error(usersError.message);
    }
  }, [toast, usersError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, sorting.key, sorting.direction]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetching: isFetchingUsers,
    isError: !!usersError,
  });

  const flattenedData = useMemo(() => users?.pages.flatMap((page) => page.data) ?? [], [users]);

  const handleDelete = () => {
    ApiService.DeleteUsers({ userIds: selectedUsers.map((user) => user.id) })
      .then(() => {
        toast.success('Sitemate users removed successfully');
        setShowRemoveConfirmation(false);
        setSelectedRows({});
        queryClient.invalidateQueries({ queryKey: sitemateUserKeys.all });
      })
      .catch((e: Error) => toast.error(e.message));
  };

  const { canExportCsv } = usePermissions();
  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function buildSitemateUsersExportRequest(): RegisterExportQuery<'users'> {
    const selected = getSelectedUsers(users);
    return {
      queryType: 'users',
      filters: { id: selected.map((user) => user.id), userType: 'sitemate-users' },
      orderBy: sorting.key ? { field: sorting.key as NonNullable<GetUsersRequestDTO['orderBy']>['field'], direction: sorting.direction ?? 'asc' } : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!users?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildSitemateUsersExportRequest(), filename: buildCsvExportFilename('sitemate-users') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!users?.pages) return;
    openExportTab(buildPdfExportJob({ scope, records: [], register: buildSitemateUsersExportRequest() }));
  }

  const { data: sitemateUsersStatsData } = useQuery(getSitemateUsersStatsQueryOptions());
  const sitemateUsersStats = sitemateUsersStatsData?.usersStats;

  return (
    <div className="bg-grey-1 flex flex-1 flex-col px-6">
      <div className="mx-auto flex h-full w-full flex-1 flex-col gap-4 py-6">
        <div className="flex h-full flex-col gap-4 rounded-md bg-white px-4 py-4 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-medium">
              Sitemate Users
              {sitemateUsersStats?.sitemateUsers?.active ? (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger type="button">
                      <span className="text-grey-5 ml-1 text-base">({sitemateUsersStats?.sitemateUsers?.active})</span>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
                        <TooltipArrow className="fill-default-text" />
                        {sitemateUsersStats?.sitemateUsers?.active} active users
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                ``
              )}
            </h3>
            <Link
              to="https://help.sitemate.com/en/articles/7851421-how-your-global-sitemate-profile-works"
              target="_blank"
              className="bg-grey-1 inline-flex w-auto gap-1 rounded p-1 text-sm font-medium">
              <QuestionIcon className="h-4 w-4" />
              Learn more
            </Link>
          </div>
          <div className="text-sm">These Sitemate users are the workforce members who have accessed your assets in the field.</div>

          <div>
            <div className="border-grey-3 flex flex-1 flex-col rounded-t border-x border-y">
              <div className="bg-grey-2 flex flex-shrink-0 items-center gap-2 overflow-x-hidden rounded-t px-2 py-1">
                {selectedUsers.length === 0 && (
                  <Input
                    className="w-[300px]"
                    prefix={<SearchIcon />}
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                )}
                {selectedUsers.length > 0 && (
                  <>
                    {canExportCsv && (
                      <Button variant="ghost" icon={<DownloadCsvIcon />} className="bg-grey-1 hover:bg-grey-3 px-2" onClick={handleCsvExport}>
                        Export CSV
                      </Button>
                    )}
                    {canExportCsv && isRegisterPdfExportEnabled && <DownloadPdfMenu variant="inline" onSelect={handlePdfScopeSelect} />}
                    <Button
                      variant="ghost"
                      icon={<DeleteIcon className="text-default-red" />}
                      className="bg-grey-1 hover:bg-grey-3 px-2"
                      onClick={() => setShowRemoveConfirmation(true)}>
                      Remove
                    </Button>
                  </>
                )}
              </div>
              <div className="relative overflow-x-auto overflow-y-hidden border-t">
                <TablePageTable
                  columns={createSitemateUsersTableColumns()}
                  data={flattenedData || []}
                  loading={(isFetchingUsers && !isFetchingNextUsersPage) || isLoadingPageConfig}
                  tableClassName="table-fixed [&_tr:last-child_td]:border-b-0 min-w-[1024px]"
                  cellClassName="border-x-transparent"
                  headClassName="!border-none"
                  headerClassName="after:!hidden before:!hidden"
                  sortButtonClassName="justify-start"
                  onRowSelectionChange={setSelectedRows}
                  rowSelection={selectedRows}
                  initialSorting={initialSorting}
                  onSortingChange={(sorting) =>
                    updatePageConfig({
                      table: { sorting: sorting },
                    })
                  }
                  globalFilter={searchTerm}
                  notFoundDisplay={
                    isFetchingUsers || debouncedSearch !== '' ? (
                      <NoSitemateUsersFoundFromSearchDisplay />
                    ) : !isFetchingUsers && debouncedSearch === '' ? (
                      <NoSitemateUsersAvailableDisplay />
                    ) : (
                      <></>
                    )
                  }
                />
              </div>
            </div>
            {hasNextUsersPage && (
              <div className="border-grey-3 w-full border">
                <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
                  {isFetchingNextUsersPage ? 'Loading more...' : 'Load Newer'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {showRemoveConfirmation && (
          <Dialog open={showRemoveConfirmation} onOpenChange={(open) => !open && setShowRemoveConfirmation(false)}>
            <DialogContent className="flex h-fit min-w-[500px] flex-col gap-4">
              <DialogHeader>
                <DialogTitle>&nbsp;</DialogTitle>
              </DialogHeader>
              <div className="flex w-full justify-center text-xl font-medium">Remove selected users?</div>
              <div className="flex w-full justify-center text-sm font-normal">Are you sure you want to remove access for the selected users?</div>
              <div className="flex w-full justify-center gap-2">
                <Button type="button" size="sm" variant="destructive" onClick={handleDelete} className="gap-1">
                  <DeleteIcon /> Remove
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowRemoveConfirmation(false)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function NoSitemateUsersFoundFromSearchDisplay() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white px-4 py-16">
      <img src={manholeShrugVector} alt="User" className="h-28" />
      <span className="text-xl leading-[30px] font-medium">Oops! No results</span>
    </div>
  );
}

function NoSitemateUsersAvailableDisplay() {
  return (
    <div className="items-left flex w-full flex-col gap-1 bg-white p-2">
      <div className="text-secondary-text text-sm font-normal">No Sitemate users have been added to this Gearbelt workspace yet</div>
    </div>
  );
}

export default SitemateUsersPage;
